import { format } from "date-fns";
import moment from "moment";
import { get as _getValue, unescape } from "lodash-es";
import { captureException } from "extensions/sentry";

import { store } from "common/store";
import strings from "common/strings";
import { number, rate, amount, cleanAmount } from "common/formats";

import { getters } from "common/components/getters/getters";
import { references } from "common/helpers/references";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { getCurrencyDecimals } from "common/helpers/currency";

const isNullOrEmptyOrUndefined = (value) => typeof value === "undefined" || value === "" || value === null;

export const table_mapper = function(fullView) {
    const qualifiers = new Qualifiers({ value: store.getState().common.qualifiersStr });
    return function(field) {
        const getter = getters.findById(field.getter_ref);
        const column = {
            getter: field.getter_ref,
            cellOptions: Object.create(null),
            sortOptions: Object.create(null),
            preFormat: (obj) => _getValue(obj, getter.key),
            batch: field.collapsed_visible === false ? "expanded" : "collapsed",
            headerTooltip: field.tooltip || getter.name || ""
        };

        if (qualifiers.has(getter.pivot_alias || getter.key)) {
            column.cellOptions.filterApplied = true;
        }
        
        if (getter.pivot && getter.valueType !== "date_time") {
            column.cellOptions.queryable = true;
            column.cellOptions.pivot_alias = getter.pivot_alias;
        }

        if (getter.key?.search(/_utc/g) !== -1) {
            column.cellOptions.utc = true;
        }
        
        if (getter.decimals) {
            column.cellOptions.precision = getter.decimals;
        }

        // Check if we should convert ref to alias
        const match = getters.findById(field.getter_ref);
        if (!match) {
            // Record the exception
            captureException(new Error("Getter match is not found"), {
                extra: {
                    getter_ref: field.getter_ref
                }
            });
            
            // And continue
            return null;
        }

        if (field.plain) return column;

        switch (getter.valueType) {
        case "number": {
            column.preFormat = (obj) => {
                const content = _getValue(obj, getter.key);
                if (getter.key === "total_installments" && (!Number.isFinite(content) || content === 0 || content === 1)) {
                    return "--";
                }
                if (getter.key === "installment_number" && (!Number.isFinite(content) || content === 0)) {
                    return "--";
                }
                return number(_getValue(obj, getter.key), column.cellOptions?.precision);
            };
            break;
        }
        case "rate": {
            column.preFormat = (obj) => rate(_getValue(obj, getter.key), column.cellOptions);
            break;
        }
        case "amount": {
            column.preFormat = (obj) => amount(_getValue(obj, getter.key), { currency: _getValue(obj, match.currency_attribute), decimals: getter.decimals || 2 });
            column.css = "align-right";
            break;
        }
        case "date_time": {
            column.preFormat = (obj) => {
                const value = _getValue(obj, getter.key);
                if (!value) return "--";
                // eslint-disable-next-line sentence-case/sentence-case
                return moment(value).format(column.cellOptions.date_format || "ddd MMM D, YYYY");
            };
            break;
        }
        case "text": {
            column.preFormat = (obj) => {
                const content = _getValue(obj, getter.key);
                if (typeof content === "undefined") {
                    return "--";
                }
                
                if (content === strings.emptyTableCell) {
                    return content;
                }
                
                if (getter._id === "5b19997edabe69133a95ef6c") { // Handle long order_id
                    const maxOrderIdLength = 50;
                    if (!content) return "--";
                    if (content.length <= maxOrderIdLength) return content;
                    return `<div title="${content}">${content.substring(0, maxOrderIdLength)}...</div>`;
                }
                
                // Handle card_expiration_date mixed case which are valueType text but have a mix of date and string
                if (getter.key === "card_expiration_date" && content) {
                    if (content.length === 4) {
                        return content;
                    }
                    const testDate = new Date(content);
                    // eslint-disable-next-line sentence-case/sentence-case
                    if ((testDate !== "Invalid Date") && !Number.isNaN(testDate)) {
                        return format(testDate, "MM/yy");
                    }
                    return content;
                }

                return references.toAlias(content, field.getter_ref) || "--";
            };
            break;
        }
        default: {
            break;
        }
        }

        return column;
    };
};

const types = {
    authorizations: {
        title: "Transaction detail",
        spacers: ["5b19997edabe69133a95ef6f", "5b19997edabe69133a95ef73", "5b19997edabe69133a95ef76", "5b19997edabe69133a95ef7e", "5b19997edabe69133a95ef82"]
    },
    chargebacks: {
        title: "Chargeback detail",
        spacers: ["5b19997edabe69133a95ef97", "5b19997edabe69133a95ef9a", "5b325a2e9eebce540b2acb82", "5b19997edabe69133a95ef74", "5b19997edabe69133a95efad", "5b19997edabe69133a95ef81"]
    },
    settlements: {
        title: "Transaction detail",
        spacers: ["5b19997edabe69133a95ef92", "5b19997edabe69133a95ef6f", "5b19997edabe69133a95ef74", "5b19997edabe69133a95efab", "5b19997edabe69133a95ef7e", "5b19997edabe69133a95ef82"]
    },
    fees: {
        title: "Fee detail",
        spacers: ["5b19997edabe69133a95ef86", "5b19997edabe69133a95ef92", "5b19997edabe69133a95ef6c"]
    },
    all_fraud: {
        title: "Fraud detail",
        spacers: ["5b19997edabe69133a95ef73", "5b19997edabe69133a95efab"]
    },
    threedsflex: {
        title: "Transaction detail",
        spacers: []
    },
    fraudsight: {
        title: "Risk monitoring",
        spacers: []
    },
    get psd2_fraud() {
        return this.all_fraud;
    },
    misc: {
        title: "Misc detail",
        spacers: []
    }
};

export const single_txn_mapper = function({ html = true, txn = {}, section, fields = [] } = {}) {
    const data = types[section] || { title: "Transaction detail", spacers: [] };
    const spanShort = "<span class='na'>--</span>";

    const formatDate = (date) => {
        if (isNullOrEmptyOrUndefined(date)) {
            return void 0;
        }
        // eslint-disable-next-line sentence-case/sentence-case
        return moment(date).format("ddd MMM D, YYYY");
    };
    
    const formatAmount = (value, currency, precision) => {
        if (isNullOrEmptyOrUndefined(value)) {
            return void 0;
        }
        
        if (isNullOrEmptyOrUndefined(currency) || currency === spanShort) {
            return number(value, 2);
        }
        return cleanAmount(value, {
            decimals: precision || getCurrencyDecimals(currency),
            currency
        });
    };
    
    const formatText = (value, key) => {
        if (Number.isFinite(value)) {
            // Stringify value just in case. Example: {attempt: 1} comes as a number, but has type 'text' in getters
            return value.toString();
        }
        
        // Have to unescape html characters to use string in JSX
        const result = references.toAlias(value, key);
        if (typeof result === "string") {
            return unescape(result);
        }
        return result;
    };

    return {
        title: data.title,
        merchant: store.getState().common.merchant_config.display_name,
        date: moment().format("MMM D, YYYY"),
        fields: fields.map((f) => {
            if (f.getter_ref === "5b19997edabe69133a95efa6" && !store.getState().permissions.user_permissions.has_fx) {
                return false;
            }

            let subheader;
            if (f.getter_ref === "5b19997edabe69133a95ef9d") {
                subheader = "ORIGINAL TRANSACTION REFERENCE";
            }
            
            // TODO: We need to fix this server-side. Some txns have external_ref_id but some have transaction_id
            if (f.getter_ref === "5b19997edabe69133a95efaa") {
                return {
                    // eslint-disable-next-line sentence-case/sentence-case
                    name: "Transaction ID",
                    spacer: false,
                    value: _getValue(txn, "external_ref_id") || _getValue(txn, "transaction_id") || "--"
                };
            }
            
            const match = getters.findById(f.getter_ref);
            if (!match) {
                throw new Error(`Getter match for ${f.getter_ref} is not found`);
            }
            
            let currency;
            if (match.valueType === "amount" && Object.prototype.hasOwnProperty.call(match, "currency_attribute")) {
                try {
                    currency = _getValue(txn, match.currency_attribute);
                } catch {
                    currency = void 0;
                }
            }
            
            let value;
            try {
                // get the value
                value = _getValue(txn, match.key);
                
                // now format it based on getter type unless plain:true is set on the getter override
                if (!f.plain) {
                    switch (match.valueType) {
                    case "amount": {
                        value = formatAmount(value, currency, match.decimals);
                    
                        break;
                    }
                    case "number": {
                        let precision = match.decimals || 2;
                        if (typeof f.precision === "number") {
                            precision = f.precision;
                        }
                        value = number(value, precision);
                    
                        break;
                    }
                    case "date_time": {
                        value = formatDate(value);
                    
                        break;
                    }
                    case "text": {
                        if (f.alternateMapping) {
                            value = formatText(value, f.alternateMapping);
                        } else {
                            value = formatText(value, match.key);
                        }
                    
                        break;
                    }
                    default: { break; }
                    }
                }
            } catch {
                value = void 0;
            }
            
            if (isNullOrEmptyOrUndefined(value)) {
                value = html ? <span className="na">--</span> : "--";
            }
            
            return {
                name: match.name,
                spacer: f.spacer || data.spacers.includes(f.getter_ref),
                value,
                subheader
            };
        }).filter(Boolean)
    };
};
